import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '../../../utils/auth';
import Icon from '../../../components/AppIcon';
import {
  HexagonPatternDash,
  CrosshairMarkDash,
  DiagonalStripesDash,
  HandDrawnUnderlineDash,
  TriangleAccentDash,
  useScrollReveal,
} from './DashboardGeometricPatterns';

const AccountStatusCard = () => {
  const session = getSession();
  const navigate = useNavigate();
  const accountTier = session?.accountTier || 'free';
  const isPremium = accountTier === 'premium';

  const [cardRef, cardVisible] = useScrollReveal({ threshold: 0.1 });

  const memberSince = session?.loggedInAt
    ? new Date(session.loggedInAt)?.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

  const billingInfo = session?.billingInfo || null;

  const statusItems = [
    {
      id: 1,
      label: 'Navn',
      value: session?.name || '—',
      icon: 'User',
      color: 'var(--color-primary)'
    },
    {
      id: 2,
      label: 'E-postadresse',
      value: session?.email || '—',
      icon: 'Mail',
      color: 'var(--color-accent)'
    },
    {
      id: 3,
      label: 'By',
      value: billingInfo?.city || '—',
      icon: 'MapPin',
      color: 'var(--color-warning)'
    },
    {
      id: 4,
      label: 'Land',
      value: billingInfo?.country || '—',
      icon: 'Globe',
      color: 'var(--color-success)'
    }
  ];

  return (
    <div
      ref={cardRef}
      className="bg-card border-2 border-border rounded-[var(--radius-md)] p-4 sm:p-5 lg:p-6"
      style={{
        position: 'relative',
        overflow: 'hidden',
        opacity: cardVisible ? 1 : 0,
        transform: cardVisible ? 'translateY(0px)' : 'translateY(22px)',
        transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1), transform 0.65s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      {/* SVG geometric overlays */}
      <HexagonPatternDash style={{ top: '-40px', right: '-40px', opacity: 0.7 }} />
      <DiagonalStripesDash
        style={{ bottom: '-20px', left: '-20px', width: '140px', height: '140px', opacity: 0.6 }}
        color="#004F98"
        opacity={0.06}
      />
      <CrosshairMarkDash style={{ top: '12px', left: '12px' }} size={16} color="#FF5722" />
      <TriangleAccentDash style={{ bottom: '16px', right: '16px' }} size={36} color="#FF5722" />

      {/* Original geometric accent */}
      <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', border: '2px solid rgba(0,79,152,0.08)', borderRadius: '2px', transform: 'rotate(15deg)', pointerEvents: 'none' }} />

      <div className="flex items-center justify-between mb-4 sm:mb-5 lg:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground font-heading">
            Kontostatus
          </h2>
          <HandDrawnUnderlineDash width={100} className="mt-1" />
        </div>
        <div className="flex items-center gap-2">
          {/* Account Tier Badge */}
          <div
            className="flex items-center gap-2 px-3 py-1 border-2 rounded-[var(--radius-sm)]"
            style={isPremium
              ? { borderColor: '#F59E0B', background: 'rgba(245,158,11,0.1)' }
              : { borderColor: 'rgba(156,163,175,0.3)', background: 'rgba(156,163,175,0.06)' }
            }
          >
            <Icon name={isPremium ? 'Crown' : 'User'} size={14} color={isPremium ? '#F59E0B' : 'var(--color-muted-foreground)'} />
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ fontFamily: 'var(--font-heading)', color: isPremium ? '#F59E0B' : 'var(--color-muted-foreground)', letterSpacing: '0.1em' }}
            >
              {isPremium ? 'Premium' : 'Gratis'}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 border-2 border-primary rounded-[var(--radius-sm)]">
            <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
            <span className="text-xs sm:text-sm font-medium text-primary">Aktiv</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-5 lg:mb-6">
        {statusItems?.map((item, idx) => (
          <div
            key={item?.id}
            className="p-4 bg-muted border-2 border-transparent hover:border-primary rounded-[var(--radius-sm)] transition-all"
            style={{
              opacity: cardVisible ? 1 : 0,
              transform: cardVisible ? 'translateY(0px)' : 'translateY(14px)',
              transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${0.08 + idx * 0.07}s, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${0.08 + idx * 0.07}s, box-shadow 200ms ease-out`,
              cursor: 'default',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded flex items-center justify-center"
                style={{ background: `${item?.color}15`, transition: 'transform 250ms cubic-bezier(0.22,1,0.36,1)' }}
              >
                <Icon name={item?.icon} size={20} color={item?.color} style={{ transition: 'transform 250ms cubic-bezier(0.22,1,0.36,1)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm text-muted-foreground font-caption mb-1">
                  {item?.label}
                </div>
                <div className="text-sm sm:text-base lg:text-lg font-semibold text-foreground font-heading truncate">
                  {item?.value}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 bg-muted/50 border-2 border-border rounded-[var(--radius-sm)] mb-4">
        <div className="flex-1">
          <div className="text-xs sm:text-sm text-muted-foreground font-caption mb-1">
            Innlogget siden
          </div>
          <div className="text-sm sm:text-base font-medium text-foreground">
            {memberSince}
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs sm:text-sm text-muted-foreground font-caption mb-1">
            Faktureringsadresse
          </div>
          <div className="text-sm sm:text-base font-medium text-foreground">
            {billingInfo?.streetAddress ? `${billingInfo?.streetAddress}, ${billingInfo?.postalCode} ${billingInfo?.city}` : 'Ikke angitt'}
          </div>
        </div>
      </div>

      {/* Upgrade CTA for free users */}
      {!isPremium && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(0,79,152,0.12) 0%, rgba(255,87,34,0.08) 100%)',
            border: '2px solid rgba(0,79,152,0.25)',
            borderRadius: 'var(--radius-sm)',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <p style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '2px', letterSpacing: '0.02em' }}>
              Oppgrader til Premium
            </p>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>
              Ubegrenset tilgang · 499 kr/mnd
            </p>
          </div>
          <button
            onClick={() => navigate('/subscription-portal?tab=plans')}
            style={{
              background: 'var(--color-accent)',
              border: '2px solid var(--color-accent)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 18px',
              fontFamily: 'var(--font-heading)',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#FFFFFF',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'transform 150ms cubic-bezier(0.22,1,0.36,1), box-shadow 150ms ease-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255,87,34,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
            onMouseUp={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
          >
            Oppgrader nå
          </button>
        </div>
      )}

      {isPremium && (
        <div
          style={{
            background: 'rgba(245,158,11,0.06)',
            border: '2px solid rgba(245,158,11,0.2)',
            borderRadius: 'var(--radius-sm)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Icon name="Crown" size={16} color="#F59E0B" />
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: '#F59E0B', fontWeight: 500 }}>
            Du har full Premium-tilgang til alle funksjoner
          </p>
        </div>
      )}
    </div>
  );
};

export default AccountStatusCard;